import { createResumableUpload } from '@/shared-utilities';

import type { WidgetUtSessionState } from './project-usability-widget-recording';

type ResumableUpload = ReturnType<typeof createResumableUpload>;

const SEGMENT_CHUNK_INTERVAL_MS = 4000;

export type WidgetUtRecorderState = Pick<
  WidgetUtSessionState,
  'nextSegmentIndex' | 'elapsedMsBefore'
> & { insightId?: string | null };

function pickRecorderMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  return (
    [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ].find((type) => MediaRecorder.isTypeSupported(type)) || ''
  );
}

interface StartSegmentOptions {
  stream: MediaStream;
  /** Mints a signed resumable URL for the given segment index */
  requestUploadUrl: (segmentIndex: number) => Promise<string>;
  /** Fired when the shared screen track ends (user hit "stop sharing") */
  onTrackEnded?: () => void;
}

/**
 * Records one segment per host-page lifetime and streams it to GCS via the
 * shared resumable-upload client. `sealSegmentSync` is the pagehide path: it
 * synchronously flushes what GCS confirmed and persists the session so the
 * next page load resumes with the following segment index.
 */
export class WidgetUtSegmentRecorder<TState extends WidgetUtRecorderState> {
  private recorder: MediaRecorder | null = null;
  private upload: ResumableUpload | null = null;
  private stream: MediaStream | null = null;
  private segmentStartedAt: number | null = null;
  private sealed = false;

  constructor(
    private state: TState,
    private readonly persist: (state: TState) => void
  ) {}

  getState(): TState {
    return this.state;
  }

  setInsightId(insightId: string) {
    if (this.state.insightId === insightId) return;
    this.state = { ...this.state, insightId };
    this.persist(this.state);
  }

  /** Current position in the MERGED recording timeline, in whole seconds. */
  getCurrentRecordingTimeSec(): number {
    const currentMs = this.segmentStartedAt
      ? Date.now() - this.segmentStartedAt
      : 0;
    return Math.floor((this.state.elapsedMsBefore + currentMs) / 1000);
  }

  isRecording(): boolean {
    return this.recorder !== null && this.recorder.state === 'recording';
  }

  async startSegment({
    stream,
    requestUploadUrl,
    onTrackEnded,
  }: StartSegmentOptions): Promise<void> {
    const signedUrl = await requestUploadUrl(this.state.nextSegmentIndex);
    const upload = createResumableUpload(signedUrl);
    const mimeType = pickRecorderMimeType();
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined
    );

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        void upload.uploadChunk(event.data);
      }
    };

    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack && onTrackEnded) {
      videoTrack.addEventListener('ended', onTrackEnded, { once: true });
    }

    recorder.start(SEGMENT_CHUNK_INTERVAL_MS);
    this.recorder = recorder;
    this.upload = upload;
    this.stream = stream;
    this.segmentStartedAt = Date.now();
    this.sealed = false;
    this.persist(this.state);
  }

  /**
   * Pagehide path — must stay synchronous. Stops the recorder (its final
   * ondataavailable may or may not land) and finalizes the GCS session with a
   * keepalive request, then advances the persisted segment index so the next
   * page load records segment N+1.
   */
  sealSegmentSync() {
    if (this.sealed || !this.recorder || !this.upload) return;
    this.sealed = true;

    try {
      if (this.recorder.state !== 'inactive') {
        this.recorder.requestData();
        this.recorder.stop();
      }
    } catch {
      // recorder may already be gone mid-unload
    }

    this.upload.setRecordingStopped();
    this.upload.finalizeSync();

    if (this.upload.getConfirmedBytes() > 0) {
      const segmentMs = this.segmentStartedAt
        ? Date.now() - this.segmentStartedAt
        : 0;
      this.state = {
        ...this.state,
        nextSegmentIndex: this.state.nextSegmentIndex + 1,
        elapsedMsBefore: this.state.elapsedMsBefore + segmentMs,
      };
      this.persist(this.state);
    }
    this.recorder = null;
    this.segmentStartedAt = null;
  }

  /**
   * Graceful end (conversation over or user-initiated stop): waits for all
   * buffered chunks to reach GCS and finalizes the segment properly.
   * Returns the totals needed by the finalize mutation.
   */
  async stop(): Promise<{ segmentCount: number; durationSec: number }> {
    if (this.sealed) {
      return {
        segmentCount: this.state.nextSegmentIndex,
        durationSec: this.state.elapsedMsBefore / 1000,
      };
    }

    const recorder = this.recorder;
    const upload = this.upload;
    this.sealed = true;

    if (recorder && recorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        recorder.addEventListener('stop', () => resolve(), { once: true });
        try {
          recorder.requestData();
          recorder.stop();
        } catch {
          resolve();
        }
      });
    }

    if (upload) {
      upload.setRecordingStopped();
      await upload.waitForUploads();
      await upload.finalize();
    }

    if (upload && upload.getConfirmedBytes() > 0) {
      const segmentMs = this.segmentStartedAt
        ? Date.now() - this.segmentStartedAt
        : 0;
      this.state = {
        ...this.state,
        nextSegmentIndex: this.state.nextSegmentIndex + 1,
        elapsedMsBefore: this.state.elapsedMsBefore + segmentMs,
      };
    }
    this.recorder = null;
    this.upload = null;
    this.segmentStartedAt = null;
    this.stopTracks();

    return {
      segmentCount: this.state.nextSegmentIndex,
      durationSec: this.state.elapsedMsBefore / 1000,
    };
  }

  /** Immediate teardown for cancel — no finalization, tracks released. */
  abort() {
    this.sealed = true;
    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.stop();
      }
    } catch {
      // best-effort
    }
    this.upload?.setRecordingStopped();
    this.recorder = null;
    this.upload = null;
    this.segmentStartedAt = null;
    this.stopTracks();
  }

  private stopTracks() {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }
}
